import { DynamicPropertyType } from 'types/DynamicPropertyType';

import type Ad from './Ad';
import type AdsManager from './AdsManager';
import { type IDynamicProperty } from './DynamicProperties/IDynamicProperty';
import DynamicGroup from './DynamicProperties/Types/Group';
import store from './RootStore';
import Store from './Store';

function adMatchesFilter(namesToAllowedValues: Map<string, Set<any>>, ad: Ad) {
  for (const [propertyName, allowedValues] of namesToAllowedValues.entries()) {
    const adProperty = ad.properties.getChild(propertyName);
    // property must be present, and its value must be among the allowed
    // ones
    if (!adProperty || !allowedValues.has(adProperty.value)) return false;
  }
  return true;
}

function filterAds(
  filterProperties: ReadonlyArray<IDynamicProperty<any>>,
  ads: ReadonlyArray<Ad>
) {
  // Logic is:
  // - Each property in filterProperties nominates a .value allowed for a
  //   property by that .name. So, for example, given the following
  //   filterProperties:
  //   [
  //     { name: 'P', value: 0 },
  //     { name: 'Q', value: 1 },
  //     { name: 'P', value: 2 }
  //   ] (note two properties with the name 'P' are present)
  //
  //   This expresses the following mapping of 'allowed' values, called
  //   `namesToAllowedValues`:
  //   {
  //     P: [0, 2],
  //     Q: [1]
  //   }
  //
  //   (note that Object and Array notation are used here for clarity, but
  //   in practice this is a Map of Sets)
  //
  // - An ad matches if, for *every* `namesToAllowedValues` entry, the ad
  //   has a property by that name, and its value is *one* of the allowed
  //   values.
  //
  // Notes:
  // - In the case of no filterProperties, this allows all ads, which is the
  //   intended behavior. As such, this shouldn't need a
  //   `filterProperties.length === 0` check as a special case, (though it
  //   could be an optimization). An independent Array is returned in all
  //   cases, though the ads within are still referentially-equal.
  //
  // - This paradigm cannot work for querying nested objects, and this has
  //   been the case for the life of the app.  Nested objects are
  //   implemented as stores/DynamicProperties/Types/Group.ts#DynamicGroup.
  //   The `.value` for those are arrays, and matching is done via
  //   `Set.has`.

  const namesToAllowedValues = new Map<string, Set<any>>();
  for (const specProperty of filterProperties) {
    const valueSet = namesToAllowedValues.get(specProperty.name);
    if (valueSet) {
      valueSet.add(specProperty.value);
    } else {
      namesToAllowedValues.set(
        specProperty.name,
        new Set([specProperty.value])
      );
    }
  }

  return ads.filter((ad) => adMatchesFilter(namesToAllowedValues, ad));
}

const FILTER_QUERY_PARAM = 'filterSetName';

const updateQueryParam = (name: string | undefined) => {
  const queryParams = store.router.queryParams;

  if (name == null) {
    // This expresses removal of the parameter
    if (queryParams) {
      delete queryParams[FILTER_QUERY_PARAM];
    }
    // else if no queryParams, there's nothing to do
  } else if (queryParams) {
    queryParams[FILTER_QUERY_PARAM] = name;
  } else {
    // no existing query params, and we want to set a parameter, so create the
    // object
    store.router.queryParams = { [FILTER_QUERY_PARAM]: name };
  }
};

/**
 * The property types whose `value` is a primitive worth matching on equality.
 *
 * An allowlist rather than a denylist, so that a newly added
 * DynamicPropertyType is non-filterable until someone decides otherwise. The
 * denylist this replaced admitted every new type by default, which is how a
 * group-shaped type once came to be filterable.
 *
 * Deliberately absent, since the reasons are no longer inferable from a list
 * of exclusions:
 * - GROUP, DIMENSIONS and SVGText are group-shaped, so `value` is an
 *   array. `getExact` and `filterAds` both compare values with `===`, which
 *   never matches a separately-constructed array - so such a filter would
 *   accumulate one entry per ad and then match nothing.
 * - SVGData is a string, but raw markup: meaningless to match on equality, and
 *   unusable as a dropdown label.
 * - LABEL is the ad's display name rather than an attribute of it.
 */
const FILTERABLE_TYPES = new Set<string>([
  DynamicPropertyType.ASSET,
  DynamicPropertyType.BOOLEAN,
  DynamicPropertyType.COLOR,
  DynamicPropertyType.NUMBER,
  DynamicPropertyType.PERCENTAGE,
  DynamicPropertyType.PIXELS,
  DynamicPropertyType.TEMPLATE,
  DynamicPropertyType.TEXT,
]);

/**
 * Owns the filtering of a Creative's ad list: which constraints are on
 * right now, and which named filters the user has saved.
 *
 * A *filter* is a DynamicGroup whose properties are the constraints - see
 * `filterAds` above for how several of them combine. Four such groups are
 * held here, and they play quite different roles:
 *
 * - `available` is not a filter at all. It is the vocabulary: one property
 *   per distinct (name, type, value) seen across the creative's ads,
 *   harvested by `parseFilterPropertiesFromAd`. It is what the drawer's
 *   dropdowns offer, and what `addProperty` validates against.
 * - `saved` holds the user's named filters, one child group each. This is
 *   the only part that persists - `Creative.onBeforeSave` writes it to the
 *   `filterSets` field, and `onLoad` reads it back.
 * - `scratch` is the unnamed working filter, and is deliberately *not* a
 *   member of `saved`. Being outside `saved` is its whole distinguishing
 *   property, so `isScratch` asks by identity rather than by name.
 * - `current` points at whichever of the above is being applied -
 *   `scratch`, or one of `saved`'s children. It is a reference, not a
 *   container: assigning it selects, it never copies.
 *
 * Editing constraints always writes through `current`, which is why leaving
 * a saved filter has to mean re-pointing at `scratch` rather than clearing
 * in place. `resetToScratch` is the only correct way to do that.
 *
 * A Store, so all of the above is observable and components re-render when
 * `current` changes.
 */
export default class FilterManager extends Store {
  available = new DynamicGroup(null, 'allAvailableProperties', []);
  scratch = new DynamicGroup(null, 'scratch', []);
  saved = new DynamicGroup(null, 'filterSets', []);
  current: DynamicGroup;
  parent: AdsManager;

  proposedFilterName = '';
  showNewFilterModal = false;

  constructor(parent: AdsManager) {
    super();
    this.parent = parent;
    this.current = this.scratch;
    this.becomeObservable();
  }

  get isScratch() {
    return this.current === this.scratch;
  }

  setCurrentByName = (name: string) => {
    const filter = this.saved.getChild(name);

    if (filter instanceof DynamicGroup) {
      this.setCurrent(filter);
    } else if (filter) {
      // Only reachable from corrupt persisted data. `saved` is populated
      // both by saveCurrentAs, which adds nothing but groups, and by
      // Creative.onLoad handing setState the stored filterSets - and that
      // route builds children through the type registry, so an entry stored
      // under some other type deserializes into some other class.
      console.warn(
        `[FilterManager.setCurrentByName] Saved filter "${name}" is a ${filter.type}, not a group. Ignoring.`
      );
    }
    // A name that matches nothing is left silent: a stale bookmark or a
    // hand-edited filterSetName reaches here routinely, and there is nothing
    // to do about it but show the unfiltered list.
  };

  private setCurrent(filterDescriptor: DynamicGroup) {
    updateQueryParam(filterDescriptor.name);
    this.current = filterDescriptor;
  }

  /**
   * Leave whatever set is active and return to the scratch set, emptied.
   *
   * Both halves matter. Resetting `scratch` rather than `current` is
   * what stops a *saved* set being rewritten in place; emptying it rather
   * than merely selecting it is what stops a stale scratch filter coming
   * back the next time the user clears. Dropping filterSetName is then
   * honest - we really did leave the set.
   */
  resetToScratch = () => {
    updateQueryParam(undefined);
    this.scratch.reset();
    this.current = this.scratch;
  };

  saveCurrentAs = (name: string) => {
    if (this.saved.getChild(name)) {
      const logName = JSON.stringify(name);
      throw new Error(`Filter Set ${logName} already exists`);
    }

    const newGroup = this.saved.addGroup(name, [...this.current.properties]);
    this.setCurrent(newGroup);
    this.scratch.reset();
  };

  parseFilterPropertiesFromAd = (ad: Ad) => {
    for (const property of ad.properties.properties) {
      if (!FILTERABLE_TYPES.has(property.type)) continue;
      if (!this.available.checkExists(property)) {
        this.available.add(property.name, property.type, property.value);
      }
    }
  };

  getFilteredAds = (ads: ReadonlyArray<Ad>) => {
    return filterAds(this.current.properties, ads);
  };

  openNewFilterModal = (proposedFilterName?: string) => {
    this.showNewFilterModal = true;
    this.proposedFilterName = proposedFilterName || '';
  };

  closeNewFilterModal = () => {
    this.showNewFilterModal = false;
    this.proposedFilterName = '';
  };

  /**
   * Activates the scratch filter as a copy of the current filter, with
   * all constraints on the named property replaced with the supplied properties.
   */
  setConstraintsForName = (
    propertyName: string,
    properties: ReadonlyArray<IDynamicProperty<any>>
  ) => {
    const otherConstraints = this.current.properties.filter(
      (p) => p.name !== propertyName
    );
    this.resetToScratch();
    this.addMultiple(otherConstraints);
    this.addMultiple(properties);
  };

  private addMultiple(properties: ReadonlyArray<IDynamicProperty<any>>) {
    for (const property of properties) {
      this.add(property);
    }
  }

  private add(property: IDynamicProperty<any>) {
    if (
      this.available.checkExists(property) &&
      !this.current.checkExists(property)
    ) {
      this.current.add(property.name, property.type, property.value);
    }
  }
}
